import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle, Clock, Mail, ArrowRight, Rocket, 
  Loader2, AlertCircle, PartyPopper, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarterPackSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const sessionId = searchParams.get('session_id');
  
  // Payment polling state
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [pollError, setPollError] = useState(null);
  const MAX_POLL_ATTEMPTS = 10;
  const POLL_INTERVAL = 2000;
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // If we have a session_id, this is a post-payment redirect - start polling
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = useCallback(async () => {
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      setPollError('Payment verification timed out. Please check your email for confirmation.');
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    setPollAttempts(prev => prev + 1);

    try {
      const response = await axios.get(`${API}/starter-pack/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setIsPolling(false);
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        setIsPolling(false);
        return;
      }

      // Continue polling
      setTimeout(pollPaymentStatus, POLL_INTERVAL);
    } catch (err) {
      console.error('Poll error:', err);
      // Continue polling even on error
      setTimeout(pollPaymentStatus, POLL_INTERVAL);
    }
  }, [sessionId, pollAttempts]);

  // Render payment success view
  if (sessionId && paymentStatus === 'paid') {
    return (
      <div data-testid="starter-pack-payment-success" className="min-h-screen bg-[#0A0A0A]">
        <SEO 
          title="Payment Successful - Welcome to The Hive | Queen B Logistics"
          description="Your Signature Program purchase is complete. Welcome to The Hive!"
        />
        
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center">
            {/* Success Animation */}
            <div className="w-24 h-24 bg-emerald-500/10 flex items-center justify-center mx-auto mb-8 animate-pulse">
              <PartyPopper className="w-12 h-12 text-emerald-400" />
            </div>
            
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              Payment <span className="text-emerald-400">Successful!</span>
            </h1>
            
            <p className="text-xl text-[#D4AF37] mb-6">
              Welcome to The Hive, Future Carrier!
            </p>
            
            <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
              Your Signature Program purchase is complete. We've created your Hive account 
              and you're ready to begin Phase 1 of your business launch.
            </p>
            
            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 p-6 mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Mail className="w-6 h-6 text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-bold">Check Your Email</span>
              </div>
              <p className="text-white/70 text-sm">
                We've sent your Hive login credentials and Phase 1 instructions 
                to your email. Set up your password and get started!
              </p>
            </div>
            
            {/* Phase 1 Info */}
            <div className="bg-zinc-900 border border-zinc-800 p-6 mb-8 text-left">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-[#D4AF37]" />
                Phase 1: Intake - Your First 5 Days
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-white/80">Complete your intake form with business details</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-white/80">Confirm your business name for authority filing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-white/80">Upload your government ID for verification</span>
                </li>
              </ul>
            </div>
            
            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/hive/login"
                className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
                data-testid="go-to-hive-btn"
              >
                Go to The Hive
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/"
                className="w-full sm:w-auto px-6 py-3 border border-zinc-700 text-white hover:bg-zinc-900 transition-colors text-center"
              >
                Return Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render polling state
  if (sessionId && isPolling) {
    return (
      <div data-testid="starter-pack-payment-processing" className="min-h-screen bg-[#0A0A0A]">
        <SEO title="Processing Payment - Queen B Logistics" />
        
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-8" />
            
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-4">
              Processing Your Payment
            </h1>
            
            <p className="text-zinc-400 mb-4">
              Please wait while we confirm your payment and set up your account...
            </p>
            
            <p className="text-zinc-500 text-sm">
              This usually takes just a few seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render poll error
  if (sessionId && pollError) {
    return (
      <div data-testid="starter-pack-payment-error" className="min-h-screen bg-[#0A0A0A]">
        <SEO title="Payment Verification - Queen B Logistics" />
        
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-8" />
            
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-4">
              Verification In Progress
            </h1>
            
            <p className="text-zinc-400 mb-6">
              {pollError}
            </p>
            
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 mb-8">
              <p className="text-amber-400 text-sm">
                If you completed the payment, your account has been created. 
                Please check your email for login instructions.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:dispatch@queenblogistics.com"
                className="px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors"
              >
                Contact Support
              </a>
              <Link 
                to="/"
                className="px-6 py-3 border border-zinc-700 text-white hover:bg-zinc-900 transition-colors"
              >
                Return Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render expired status
  if (sessionId && paymentStatus === 'expired') {
    return (
      <div data-testid="starter-pack-payment-expired" className="min-h-screen bg-[#0A0A0A]">
        <SEO title="Session Expired - Queen B Logistics" />
        
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-8" />
            
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-4">
              Payment Session Expired
            </h1>
            
            <p className="text-zinc-400 mb-6">
              Your checkout session has expired. Please submit a new application to continue.
            </p>
            
            <Link 
              to="/build/apply"
              className="px-8 py-4 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors inline-block"
            >
              Submit New Application
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Default: Application approved view (original behavior)
  return (
    <div data-testid="starter-pack-success-page" className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Application Approved - Queen B Logistics"
        description="Your application has been approved. Complete your payment to begin your trucking business."
      />
      
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 bg-emerald-500/10 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-emerald-400" />
          </div>
          
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
            Welcome to <span className="text-[#D4AF37]">Queen B Logistics</span>
          </h1>
          
          <p className="text-xl text-white mb-6">
            You&apos;re one step away from beginning your trucking business.
          </p>
          
          <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
            Our team has reviewed your information and <span className="text-emerald-400 font-semibold">approved your application</span>.
          </p>

          {/* What You'll Receive */}
          <div className="bg-gradient-to-r from-[#D4AF37]/15 to-[#D4AF37]/5 border border-[#D4AF37]/40 p-6 mb-8 text-left">
            <p className="text-[#D4AF37] font-bold uppercase tracking-[0.15em] text-sm mb-4 text-center">
              Once payment is completed you&apos;ll immediately receive:
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span><strong className="text-white">Portal Access</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span><strong className="text-white">Welcome Guide</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span><strong className="text-white">Phase One Tasks</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span><strong className="text-white">Dedicated Support</strong></span>
              </li>
            </ul>
          </div>
          
          {/* Email Notice */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Mail className="w-6 h-6 text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-bold">Check Your Email</span>
            </div>
            <p className="text-white/70 text-sm">
              We&apos;ve sent your secure checkout link to your email address. 
              The link expires in <strong className="text-white">48 hours</strong>.
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/"
              className="w-full sm:w-auto px-6 py-3 border border-zinc-700 text-white hover:bg-zinc-900 transition-colors text-center"
            >
              Return Home
            </Link>
            <a 
              href="mailto:support@queenblogistics.com"
              className="w-full sm:w-auto px-6 py-3 bg-zinc-800 text-white hover:bg-zinc-700 transition-colors text-center"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarterPackSuccessPage;
